import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, 
  TableHead, TableRow, Chip, Button, TextField, MenuItem, 
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../../api';

const ComponentMaster = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({ category: 'Addition', sub_category: '' });

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const res = await api.get('/payroll/components/master');
      setComponents(res.data || []);
    } catch (err) {
      const errorMsg = err.response?.status === 403 
        ? 'Access Denied: Only Admins can manage Master Components.' 
        : 'Failed to load components.';
      showNotif(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComponents(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleCreate = async () => {
    if (!formData.sub_category.trim()) {
      showNotif('Please enter a sub-category name.', 'error');
      return;
    }
    try {
      setSubmitLoading(true);
      await api.post('/payroll/components/master', formData);
      showNotif('Component created successfully.');
      setDialogOpen(false);
      setFormData({ category: 'Addition', sub_category: '' });
      fetchComponents();
    } catch (err) {
      showNotif(err.response?.data?.detail || 'Failed to create component.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
    try {
      await api.put(`/payroll/components/master/${id}`, { status: newStatus });
      showNotif(`Component marked as ${newStatus}.`);
      fetchComponents();
    } catch (err) {
      showNotif('Failed to update status.', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>Salary Component Master</Typography>
            <Typography variant="body2" color="textSecondary">Define the official catalog of Additions and Deductions.</Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setDialogOpen(true)}
          >
            Add Component
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sub-Category (Name)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {components.map((comp) => (
                <TableRow key={comp.id} hover>
                  <TableCell>
                    <Chip 
                      label={comp.category} 
                      size="small" 
                      color={comp.category === 'Addition' ? 'success' : 'error'} 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell><Typography sx={{ fontWeight: 'bold' }}>{comp.sub_category}</Typography></TableCell>
                  <TableCell>
                    <Chip label={comp.status} size="small" color={comp.status === 'Active' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color={comp.status === 'Active' ? 'warning' : 'success'}
                      onClick={() => toggleStatus(comp.id, comp.status)}
                    >
                      {comp.status === 'Active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {components.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ p: 4, color: 'text.secondary' }}>No components defined yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* CREATE DIALOG */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>New Salary Component</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          >
            <MenuItem value="Addition">Addition</MenuItem>
            <MenuItem value="Deduction">Deduction</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Sub-Category (e.g., Incentives, Insurance)"
            value={formData.sub_category}
            onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitLoading}>
            {submitLoading ? <CircularProgress size={24} /> : 'Save Component'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComponentMaster;